import {CodeIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useAnalytics} from '@github-ui/use-analytics'

import type {BaseServer} from '../../types/index'

const getMcpRegistryHostPort = () => {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    return 'localhost:8080'
  }
  return 'api.mcp.github.com/2025-09-15'
}

// create the registry server URL
export const getRegistryServerUrl = (protocol: string, mcp: BaseServer) => {
  // Use api_name for VS Code deeplinks since that's the exact name stored in the
  // MCP Registry API database. Fall back to full_name or name for backwards compatibility.
  const serverName = mcp.api_name || mcp.full_name || mcp.name
  // by name: ${protocol}:mcp/by-name/{serverName}
  if (protocol === 'vscode' || protocol === 'vscode-insiders') {
    return `${protocol}:mcp/by-name/${serverName}`
  }

  // by host port & id: ${protocol}:mcp/${hostPort}/v0/servers/${id}
  return `${protocol}:mcp/${getMcpRegistryHostPort()}/v0/servers/${mcp.id}`
}

type Props = {
  mcp: BaseServer
  muted?: boolean
}

export function InstallButton({mcp, muted}: Props) {
  const isMcpRegistryDevEnabled = isFeatureEnabled('mcp_registry_dev')
  const canonicalName = mcp.full_name || mcp.name

  const {sendAnalyticsEvent} = useAnalytics()

  const getEncodedBasicInfo = () => {
    return encodeURIComponent(
      JSON.stringify({
        name: canonicalName,
        type: 'http',
        url: mcp.url,
      }),
    )
  }

  const handleInstallWithProtocol = (protocol: string) => {
    const url = getRegistryServerUrl(protocol, mcp)
    window.open(url, '_self')
  }

  const handleInstallInVSCode = () => handleInstallWithProtocol('vscode')
  const handleInstallInVSCodeInsiders = () => handleInstallWithProtocol('vscode-insiders')
  const handleInstallInVSCodeOSS = () => handleInstallWithProtocol('code-oss')

  const handleInstallInVisualStudio = () => {
    const encodedData = getEncodedBasicInfo()
    window.open(`vsweb+mcp:/install?${encodedData}`, '_self')
  }

  return (
    <ActionMenu>
      <ActionMenu.Button
        variant={muted ? 'default' : 'primary'}
        block={!muted}
        size={muted ? 'small' : 'large'}
        onClick={() =>
          sendAnalyticsEvent('analytics.click', '', {
            referrer: `${window.location.origin}/mcp/${mcp.name}`,
            text: `${mcp.name} install`,
          })
        }
      >
        {muted ? 'Install' : 'Install MCP server'}
      </ActionMenu.Button>

      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={handleInstallInVSCode}>
            <ActionList.LeadingVisual>
              <CodeIcon size={16} />
            </ActionList.LeadingVisual>
            Install in VS Code
          </ActionList.Item>

          <ActionList.Item onSelect={handleInstallInVSCodeInsiders}>
            <ActionList.LeadingVisual>
              <CodeIcon size={16} />
            </ActionList.LeadingVisual>
            Install in VS Code Insiders
          </ActionList.Item>

          {isMcpRegistryDevEnabled && (
            <ActionList.Item onSelect={handleInstallInVSCodeOSS}>
              <ActionList.LeadingVisual>
                <CodeIcon size={16} />
              </ActionList.LeadingVisual>
              Install in VS Code development environment
            </ActionList.Item>
          )}

          {isMcpRegistryDevEnabled && (
            <ActionList.Item onSelect={handleInstallInVisualStudio}>
              <ActionList.LeadingVisual>
                <CodeIcon size={16} />
              </ActionList.LeadingVisual>
              Install in Visual Studio
            </ActionList.Item>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
